import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Plus, Trash2 } from "lucide-react";

export interface SmartTableColumn {
  key: string;
  label: string;
  /** "text" | "number" | "currency" — number/currency columns are summed in the totals row */
  type?: "text" | "number" | "currency";
  placeholder?: string;
  /** Optional fixed width, e.g. "8rem" */
  width?: string;
}

export interface SmartTableConfig {
  columns?: SmartTableColumn[];
  /** Rows shown when the user has no saved data yet */
  initialRows?: Array<Record<string, string | number>>;
  allowAddRows?: boolean;
  allowDeleteRows?: boolean;
  showTotals?: boolean;
  showRowNumbers?: boolean;
  addRowLabel?: string;
  /** Minimum number of empty rows to render initially */
  minRows?: number;
}

interface SmartTableValue {
  rows: Array<Record<string, string | number>>;
  totals: Record<string, number>;
  columns: Array<{ key: string; label: string; type?: string }>;
}

interface SmartTableProps {
  config?: SmartTableConfig;
  content: { key: string; label?: string };
  userData?: Record<string, any>;
  onChange?: (key: string, value: SmartTableValue) => void;
  readonly?: boolean;
  /** Activity language ("he"/"en"/"de") — wins over the app UI language */
  lang?: string;
}

const numeric = (v: string | number | undefined): number => {
  if (v === undefined || v === null) return 0;
  const n = Number(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const formatTotal = (n: number, type?: string) =>
  type === "currency" ? `${n.toLocaleString("he-IL")} ₪` : n.toLocaleString("he-IL");

const emptyRow = (columns: SmartTableColumn[]) =>
  Object.fromEntries(columns.map((c) => [c.key, ""]));

/**
 * SmartTable — a "mini excel": editable table with configurable columns,
 * add/remove rows and an automatic totals row for numeric columns.
 * Saved value is self-describing ({rows, totals, columns}) so AI prompts
 * and other components (e.g. image extraction) can consume it directly.
 */
const SmartTable = ({ config = {}, content, userData, onChange, readonly = false, lang }: SmartTableProps) => {
  const { i18n, t } = useTranslation();
  const dir = lang ? (lang === "he" ? "rtl" : "ltr") : i18n.dir();

  const columns: SmartTableColumn[] = useMemo(
    () => (Array.isArray(config.columns) && config.columns.length ? config.columns : []),
    [config.columns]
  );

  const saved: SmartTableValue | undefined = userData?.[content.key];

  const [rows, setRows] = useState<Array<Record<string, string | number>>>(() => {
    if (saved?.rows?.length) return saved.rows;
    if (config.initialRows?.length) return config.initialRows;
    const min = Math.max(1, config.minRows ?? 1);
    return Array.from({ length: min }, () => emptyRow(columns));
  });

  // Merge external row injections (e.g. from the image-extract component):
  // when the saved value carries more rows than we hold, adopt them.
  useEffect(() => {
    if (saved?.rows && saved.rows !== rows && (saved as any).__injected) {
      setRows(saved.rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const totals = useMemo(() => {
    const out: Record<string, number> = {};
    for (const c of columns) {
      if (c.type === "number" || c.type === "currency") {
        out[c.key] = rows.reduce((sum, r) => sum + numeric(r[c.key]), 0);
      }
    }
    return out;
  }, [rows, columns]);

  useEffect(() => {
    if (readonly || !onChange) return;
    onChange(content.key, {
      rows,
      totals,
      columns: columns.map(({ key, label, type }) => ({ key, label, type })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const setCell = (rowIdx: number, colKey: string, value: string) => {
    setRows((old) => old.map((r, i) => (i === rowIdx ? { ...r, [colKey]: value } : r)));
  };

  const addRow = () => setRows((old) => [...old, emptyRow(columns)]);
  const deleteRow = (idx: number) => setRows((old) => old.filter((_, i) => i !== idx));

  if (!columns.length) return null;

  const hasTotals = config.showTotals !== false && Object.keys(totals).length > 0;
  const canDelete = !readonly && config.allowDeleteRows !== false && rows.length > 1;

  return (
    <div dir={dir} className="my-3 min-w-0 max-w-full" data-field-id={content.key}>
      {content.label && (
        <Label className="mb-2 block text-sm font-medium">{content.label}</Label>
      )}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-muted/60">
              {config.showRowNumbers !== false && (
                <th className="w-8 px-2 py-2 text-xs font-medium text-muted-foreground border-b border-border">
                  #
                </th>
              )}
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={c.width ? { width: c.width } : undefined}
                  className="px-2 py-2 text-start text-xs font-semibold border-b border-border"
                >
                  {c.label}
                </th>
              ))}
              {canDelete && <th className="w-8 border-b border-border" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="odd:bg-background even:bg-muted/20">
                {config.showRowNumbers !== false && (
                  <td className="px-2 py-1 text-center text-xs text-muted-foreground border-b border-border/50">
                    {rowIdx + 1}
                  </td>
                )}
                {columns.map((c) => (
                  <td key={c.key} className="border-b border-border/50 p-0">
                    {readonly ? (
                      <div className="px-2 py-1.5">{row[c.key] ?? ""}</div>
                    ) : (
                      <input
                        dir={c.type === "number" || c.type === "currency" ? "ltr" : dir}
                        inputMode={c.type === "number" || c.type === "currency" ? "decimal" : undefined}
                        className="w-full min-w-[6rem] bg-transparent px-2 py-1.5 outline-none focus:bg-primary/5 focus:ring-1 focus:ring-primary/30 rounded-sm"
                        value={row[c.key] ?? ""}
                        placeholder={c.placeholder ?? ""}
                        onChange={(e) => setCell(rowIdx, c.key, e.target.value)}
                      />
                    )}
                  </td>
                ))}
                {canDelete && (
                  <td className="border-b border-border/50 text-center">
                    <button
                      type="button"
                      aria-label={t("delete") || "Delete"}
                      className="p-1 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteRow(rowIdx)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {hasTotals && (
            <tfoot>
              <tr className="bg-muted/60 font-semibold">
                {config.showRowNumbers !== false && <td className="border-t border-border" />}
                {columns.map((c, i) => (
                  <td key={c.key} className="px-2 py-2 border-t border-border" dir="ltr" style={{ textAlign: dir === "rtl" ? "right" : "left" }}>
                    {totals[c.key] !== undefined
                      ? formatTotal(totals[c.key], c.type)
                      : i === 0
                        ? (dir === "rtl" ? 'סה"כ' : "Total")
                        : ""}
                  </td>
                ))}
                {canDelete && <td className="border-t border-border" />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {!readonly && config.allowAddRows !== false && (
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addRow}>
          <Plus className="h-4 w-4 me-1" />
          {config.addRowLabel ?? (dir === "rtl" ? "הוסף שורה" : "Add row")}
        </Button>
      )}
    </div>
  );
};

export default SmartTable;
